import React, { useState, useRef, useEffect } from "react";
import { useCountry } from './../CountryContext';
import { AnimatePresence, motion } from "framer-motion";
import ReportsSection from "../../components/ReportDropdown8"

const Dropdown = ({ type, value, options, onSelect, countryValue }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownMenuRef = useRef(null);
    const contentRef = useRef(null);
    const thumbRef = useRef(null);
    const [thumbHeight, setThumbHeight] = useState(20);
    const [scrollTop, setScrollTop] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [selectedItems, setSelectedItems] = useState({});
  
    // Add your specific options based on the type
    const dropdownOptions = type === "SERVICES" ? [
      { id: "contracts", name: "Contracts", value: "contracts" },
      { id: "calendar", name: "Calendar", value: "calendar" },
      { id: "verification", name: "Verification", value: "verification" },
      { id: "crop-advisory", name: "Crop Advisory", value: "crop-advisory" },
      { id: "livestock-advisory", name: "Livestock Advisory", value: "livestock-advisory" },
      { id: "pest-alert", name: "Pest Alert", value: "pest-alert" },
      { id: "disease-alert", name: "Disease Alert", value: "disease-alert" },
      { id: "credit-score", name: "Credit Score", value: "credit-score" },
      { id: "weather", name: "Weather", value: "weather" }
    ] : options || [];
  
    const capitalizeWords = (str) => {
      if (!str) return "";
      return str
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
    };
  
    const getDisplayValue = () => {
      if (value) {
        if (type === "COUNTRY") return value.name || "-";
        return value.name || value.value || "-";
      }
      return "-";
    };
  
    const getHeaderTitle = () => {
      switch (type) {
        case "COUNTRY":
          return "Countries";
        case "REGION":
          return "Regions";
        case "DISTRICT":
          return "Districts";
        case "MUNICIPALITY":
          return "Municipalities";
        case "SMMEs":
          return "SMMEs";
        case "VALUE CHAIN":
          return "Value Chains";
        case "PROJECT":
          return "Projects";
        case "USER TYPE":
          return "User Types";
        case "SERVICES":
          return "Services";
        default:
          return "Select Option";
      }
    };
  
    const toggleItemSelection = (itemId) => {
      setSelectedItems(prev => ({
        ...prev,
        [itemId]: !prev[itemId]
      }));
    };
  
    const renderRegularItems = () => {
      return dropdownOptions?.map((option, index) => (
        <li
          key={index}
          className={`group flex items-center justify-between px-3 py-2 text-sm cursor-pointer hover:bg-[#2E453D] transition-colors duration-200 rounded ${
            selectedItems[option.id] ? "bg-[#2E453D]" : ""
          }`}
        >
          <span 
            className="text-white"
            onClick={(e) => {
              e.stopPropagation();
              onSelect(option);
              setIsOpen(false);
            }}
          >
            {option.name || option.value}
          </span>
          <button 
            onClick={(e) => {
              e.stopPropagation();
              toggleItemSelection(option.id);
            }}
          >
            {selectedItems[option.id] ? <SelectedIcon /> : <UnselectedIcon />}
          </button>
        </li>
      ));
    };
  
    const renderGroupedItems = () => {
      return renderRegularItems();
    };
  
    const UnselectedIcon = () => (
      <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="8.5" cy="8.5" r="8" fill="#D9D9D9" stroke="none"/>
      </svg>
    );
    
    const SelectedIcon = () => (
      <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="8.5" cy="8.5" r="8" fill="#2E453D" stroke="#FFBD26"/>
        <path d="M5 8.5L7.5 11L12 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    );
  
    const handleScroll = () => {
      if (!contentRef.current) return;
      const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
      setScrollTop(scrollTop);
      
      const thumbH = Math.max(
        (clientHeight / scrollHeight) * clientHeight,
        20
      );
      setThumbHeight(thumbH);
    };
  
    const handleTrackClick = (e) => {
      if (!contentRef.current || !thumbRef.current) return;
      const trackRect = e.currentTarget.getBoundingClientRect();
      const thumbOffset = e.clientY - trackRect.top - thumbHeight / 2;
      const scrollPercentage = thumbOffset / (trackRect.height - thumbHeight);
      contentRef.current.scrollTop =
        scrollPercentage * (contentRef.current.scrollHeight - contentRef.current.clientHeight);
    };
  
    const handleThumbMouseDown = (e) => {
      e.preventDefault();
      setIsDragging(true);
      
      const startY = e.clientY;
      const startScrollTop = scrollTop;
      
      const handleMouseMove = (e) => {
        if (!isDragging || !contentRef.current) return;
        const deltaY = e.clientY - startY;
        const trackHeight = contentRef.current.clientHeight - thumbHeight;
        const scrollDelta = (deltaY / trackHeight) * contentRef.current.scrollHeight;
        contentRef.current.scrollTop = startScrollTop + scrollDelta;
      };
      
      const handleMouseUp = () => {
        setIsDragging(false);
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
      
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    };
  
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (dropdownMenuRef.current && !dropdownMenuRef.current.contains(event.target)) {
          setIsOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);
  
    useEffect(() => {
      if (contentRef.current) {
        handleScroll();
      }
    }, [isOpen, options]);
  
    return (
        <div className="relative md:mr-4 mr-2" ref={dropdownMenuRef}>
        <button
          className={`relative xl:w-[205px] lg:w-[160px] md:w-[100px] w-[70px] h-[20px] md:h-[53px] bg-[#2E453D] text-white flex justify-between items-center text-[8px] md:text-[17px] px-[2px] rounded-[4px] truncate`}
          onClick={() => setIsOpen(!isOpen)}
        >
          <span className={`whitespace-normal break-words font-normal leading-[0.90] text-center capitalize w-full ${
            getDisplayValue() === "-" ? "text-gray-400" : ""
          }`}>
            {capitalizeWords(getDisplayValue())}
          </span>
          
          <span className="absolute bottom-[1px] right-3 text-[6px] md:text-[8px] text-white">
            {(type === "COUNTRY" && value?.code) || 
             (type === "REGION" && value?.value) || 
             (type === "DISTRICT" && value?.value) || 
             (type === "SMMEs" && value?.value) || 
             '-'}
          </span>
          
          <img
            src="https://res.cloudinary.com/dzngpgki4/image/upload/v1751799445/Expand_down_light_syoynq.png"
            alt="expand"
            className="absolute bottom-2 right-2 w-2 h-2 md:w-5 md:h-5"
          />
        </button>
  
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
              className="bg-[#2E453D]/80 rounded-b-[5px] overflow-hidden backdrop-blur-sm absolute top-full mt-1 left-0 w-full z-50 md:w-[205px]"
            >
              <div className="px-3 py-2">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-white text-sm font-semibold">
                    {getHeaderTitle()}
                  </h3>
                  <img 
                    src="https://res.cloudinary.com/dzngpgki4/image/upload/v1750628487/Export_light_kuxwb6.png" 
                    alt="Export icon"
                    className="w-4 h-4 ml-2 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsOpen(false);
                    }}
                  />
                </div>
                <div className="custom-scroll-area max-h-[200px]">
                  <div
                    className="custom-scroll-content"
                    ref={contentRef}
                    onScroll={handleScroll}
                  >
                    <ul className="space-y-2">
                      {type === "SMMEs" || type === "TYPE OF FARMING" 
                        ? renderGroupedItems()
                        : renderRegularItems()}
                    </ul>
                  </div>
                  <div className="custom-scroll-track" onClick={handleTrackClick}>
                    <div
                      ref={thumbRef}
                      className="custom-scroll-thumb"
                      style={{
                        height: `${thumbHeight}px`,
                        top: `${scrollTop}px`,
                        cursor: isDragging ? "grabbing" : "grab",
                      }}
                      onMouseDown={handleThumbMouseDown}
                    ></div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };
const ContractMain = () => {
  const renderButtonWithIcon = (text, bgColor, iconColor, isLarge = false) => {
    const bgColorClasses = {
      "#152C24": "bg-[#152C24]",
      "#2E453D": "bg-[#2E453D]",
      "#FDBB26": "bg-[#FDBB26]",
    };

    const iconColorClasses = {
      "#152C24": "bg-[#152C24]",
      "#2E453D": "bg-[#2E453D]",
      "#FDBB26": "bg-[#FDBB26]",
    };

    return (
      <div className="flex items-center">
        <div className={`${bgColorClasses[bgColor]} text-white md:text-[16px] text-[5px] px-2 md:px-4 ${
          isLarge ? "md:py-[14px] py-[3px]" : "md:py-[14px] py-[3px]"
        } md:rounded-l-md rounded-l-md font-bold md:w-[300px] whitespace-nowrap flex items-center h-full`}>
          {text}
        </div>
        
        <div className={`${iconColorClasses[iconColor]} hidden md:flex items-center md:py-2  justify-center md:w-[57px] md:h-[52px] rounded-r-md`}>
          <img
            src="https://res.cloudinary.com/dzngpgki4/image/upload/v1744139834/Expand_right_2_a4wodr.png"
            alt="Expand"
            className="w-[20px] h-[6px] md:h-[40px] md:w-[55px]  object-contain"
          />
        </div>
        
        <div className={`${iconColorClasses[iconColor]} md:hidden flex items-center justify-center w-[20px]  h-[14px] rounded-r-md`}>
          <img
            src="https://res.cloudinary.com/dzngpgki4/image/upload/v1744370865/Expand_right_5_l2cjmf.png"
            alt="Expand"
            className="w-[20px] h-[6px]  object-contain"
          />
        </div>
      </div>
    );
  };
  const handleReportDropdownChange = (type, value) => {
    setReportSelections(prev => {
      const newSelections = {
        ...prev,
        [type]: value
      };
      
      if (type === 'COUNTRY') {
        newSelections.REGION = "-";
        newSelections.DISTRICT = "-";
        newSelections.MUNICIPALITY = "-";
      }
      
      return newSelections;
    });
  };
  const { selectedCountry } = useCountry();
  const [selectedService, setSelectedService] = useState(null);
  const [reportSelections, setReportSelections] = useState({
    COUNTRY: "-",
    REGION: "-",
    DISTRICT: "-",
    MUNICIPALITY: "-",
    SMMEs: "-",
    "VALUE CHAIN": "-",
    PROJECT: "-",
    "USER TYPE": "-"
  });
  
  const dropdownOptions = [
    { id: 1, name: "Option 1", value: "opt1" },
    { id: 2, name: "Option 2", value: "opt2" },
    { id: 3, name: "Option 3", value: "opt3" },
  ];

  const handleDropdownSelect = (option) => {
    console.log("Selected:", option);
  };

  return (
    <div className='bg-white'>
      <div className='md:px-20 px-4 md:pt-6 pt-2 md:pb-5 pb-2'>
        <div className="flex justify-between items-center">
          <h2 className="md:text-[36px] text-[10px] capitalize text-[#152C24] font-bold">
            <span className="text-[#979797] capitalize">SERVICES | </span>{selectedCountry?.name || 'South Africa'}
          </h2>
          <div>
            <h1 className="md:text-[20px] text-[8px] font-extrabold md:mb-2">USER NUMBER</h1>
            <div className="flex w-fit">
              <button
                className={`xl:w-[250px] lg:w-[160px] md:w-[90px] w-[70px] h-[20px] md:h-[53px] bg-[#152C24] text-white font-medium flex items-center px-1 rounded-l-[5px]`}>
                <input className="md:text-[18px] text-[8px] md:px-2 bg-transparent outline-none placeholder-white" placeholder="XXXXXXXXX"/>
              </button>
              <div
                className="w-[16px] h-[20px] md:h-[53px] md:w-[50px] bg-[#EEB527] text-white flex justify-center items-center rounded-r-[5px] -ml-2 z-30 cursor-pointer"
              >
                <img
                  src="https://res.cloudinary.com/dzngpgki4/image/upload/v1747251939/Expand_right_wbmjno.png"
                  alt="expand"
                  className="w-3 h-[10px] md:w-6 md:h-[24px]"
                />
              </div>
            </div>
            <p className="md:text-[14px] text-[6px] font-normal mt-1 text-black">
              Insert ID Number
            </p>
          </div>
        </div>
        
        <div className="flex items-center w-full md:mb-4 md:mt-6 mb-2 gap-4 md:gap-12">
          <Dropdown 
  type="SERVICES" 
  value={selectedService} 
  onSelect={(option) => setSelectedService(option)}
          />
          
          <div className="flex-1 relative">
            <input
              placeholder="Search"
              type="text"
              className="w-full md:h-[61px] h-[25px] text-[#979797] text-[8px] md:text-[16px] font-semibold md:p-3 pr-8 p-1 md:px-6 px-3 border border-[#C4C4C4] md:rounded-md rounded-full focus:outline-none focus:ring-2 focus:ring-green-600"
            />
            <img
              src="https://res.cloudinary.com/dzngpgki4/image/upload/v1744490602/Search_k7vxkr.png"
              alt="Search Icon"
              className="w-[12px] h-[12px] md:h-[24px] md:w-[24px] absolute right-3 top-1/2 transform -translate-y-1/2"
            />
          </div>
        </div>
        <ReportsSection
        values={reportSelections}
        onChange={handleReportDropdownChange}
      />
        <h2 className="md:text-[36px] text-[10px] capitalize text-[#152C24] md:mt-6 font-bold">
Contracts
        </h2>
        
        <div className="relative w-full h-[150px] md:h-[650px]">
          <img
            src="https://res.cloudinary.com/dwfdzdaxk/image/upload/v1752760167/image_1875_vbsf0r.png "
            alt="Securing Agriculture in Namibia"
            className="w-full h-full object-cover md:rounded-lg rounded-md transition-opacity duration-500"
          />
        </div>
        
        <div className="flex justify-between items-center md:mt-5 mt-2 mb-2">
          <h2 className="md:text-[36px] text-[10px] capitalize text-[#152C24] md:mt-6 font-bold">
          About Contracts
          </h2>
       <a href="/Contract1">
       <div>
            {renderButtonWithIcon("Contracts", "#2E453D", "#152C24", true)}
            <p className="md:text-[14px] text-[6px] font-normal mt-1">Click to go to contract</p>
          </div>
       </a>
        </div>
        <div className="font-sans md:text-[32px] text-[10px] leading-[1.6] text-black space-y-4 md:space-y-8 py-4 md:py-10 bg-white">
  <p>
    The FarmNet website serves as a digital platform for connecting smallholder and emerging farmers with institutional buyers, waste-to-farm initiatives, and sustainability-driven food systems...
  </p>

  <p className="font-bold">
    How It Works: <span className="font-normal">Offtake Contracting via FarmNet</span>
  </p>

  {/* Step 1 */}
  <div>
    <p className="font-bold">1. Farmer Registration:</p>
    <ul className="list-disc ml-12">
      <li>Farmers register on the FarmNet website by submitting:</li>
    </ul>
    <ul className="list-disc ml-6 md:ml-20 space-y-1 md:space-y-2">
      <li>Farm profile (location, size, crops)</li>
      <li>Production capacity and seasonality</li>
      <li>Soil test records (if available)</li>
      <li>Compliance with organic or regenerative practices</li>
    </ul>
    <p className="ml-6 md:ml-12">Once verified by the FarmNet team, the farmer profile is listed as "contract-ready."</p>
  </div>

  {/* Step 2 */}
  <div>
    <p className="font-bold">2. Contract Opportunities Posted:</p>
    <ul className="list-disc ml-4">
      <li>Buyers (e.g. Netcare hospitals, restaurants, retailers) post offtake contract offers on the portal, including:</li>
    </ul>
    <ul className="list-disc ml-6 md:ml-20 space-y-1 md:space-y-2">
      <li>Crop types and volumes required</li>
      <li>Delivery frequency and location</li>
      <li>Pricing models (fixed, market-linked, or incentive-based)</li>
      <li>Quality and certification requirements</li>
    </ul>
  </div>

  {/* Step 3 */}
  <div>
    <p className="font-bold">3. Matching & Bidding:</p>
    <ul className="list-disc ml-12">
      <li>Farmers can apply or bid for contracts suited to their farm capacity. The system matches based on:</li>
    </ul>
    <ul className="list-disc ml-6 md:ml-20 space-y-1 md:space-y-2">
      <li>Location</li>
      <li>Crop readiness</li>
      <li>Past delivery performance or ratings (if applicable)</li>
    </ul>
  </div>

  {/* Step 4 */}
  <div>
    <p className="font-bold">4. Review & Approval:</p>
    <ul className="list-disc ml-10">
      <li>The FarmNet contracting team reviews the application and conducts:</li>
    </ul>
    <ul className="list-disc ml-6 md:ml-20 space-y-1 md:space-y-2">
      <li>Site visit (if necessary)</li>
      <li>Yield forecast confirmation</li>
      <li>Traceability system training</li>
    </ul>
  </div>

  {/* Step 5 */}
  <div>
    <p className="font-bold">5. Digital Contract Signing:</p>
    <ul className="list-disc ml-4">
      <li>Contracts are signed electronically and stored on the FarmNet portal. Farmers are assigned a Field Support Officer to help deliver on the contract.</li>
    </ul>
  </div>

  {/* Benefits to Farmers */}
  <div>
    <p className="font-bold">Benefits to Farmers</p>
    <ul className="list-disc ml-4 md:ml-20 space-y-1 md:space-y-2">
      <li>Guaranteed market access to high-value buyers</li>
      <li>Predictable income streams through forward contracts</li>
      <li>Technical support (e.g., compost use, organic transition help)</li>
      <li>Access to certification pathways via platform-linked partners</li>
      <li>Digital records & traceability, improving chances of securing future funding or contracts</li>
    </ul>
  </div>

  {/* Benefits to Partners */}
  <div>
    <p className="font-bold">Benefits to Partners (Buyers & ESG Stakeholders)</p>
    <ul className="list-disc ml-4 md:ml-20 space-y-1 md:space-y-2">
      <li>Verified supply chain transparency from soil to plate</li>
      <li>ESG impact tracking, including waste diversion, carbon savings, and farmer development</li>
      <li>Consistent access to fresh, organic produce with traceable quality standards</li>
      <li>Partnership visibility via FarmNet's dashboard and reporting tools</li>
      <li>Brand differentiation through participation in a regenerative farming network</li>
    </ul>
  </div>

  {/* Key Features */}
  <div>
    <p className="font-bold">Key Features of the FarmNet Website Platform</p>
    <ul className="list-disc ml-4 md:ml-20 space-y-1 md:space-y-2">
      <li><span className="font-bold">Mobile Record-Keeping:</span> Easy, on-the-go tracking of farm activities...</li>
      <li><span className="font-bold">Verified Service Provider Network:</span> Direct access to trusted input suppliers...</li>
      <li><span className="font-bold">Market Access:</span> Connects farmers to institutional buyers...</li>
      <li><span className="font-bold">Integrated Payments:</span> Secure, streamlined payments for produce sales...</li>
    </ul>
  </div>
</div>



        <div className="flex justify-end">
        <a href="/Contract1">
       <div>
            {renderButtonWithIcon("Contracts", "#2E453D", "#152C24", true)}
            <p className="md:text-[14px] text-[6px] font-normal mt-1">Click to go to contract</p>
          </div>
       </a>
        </div>
        
        <div className="flex justify-end md:mt-6 mt-3">
          <div className="flex gap-1 md:gap-2 items-center">
            <h1 className="md:text-[24px] text-[8px] font-normal">WhatsApp us</h1>
            <img src="https://res.cloudinary.com/dzngpgki4/image/upload/v1752160024/image_1785_bsrl4k.png" alt="link" className="w-[12px] h-[12px] md:w-[48px] md:h-[48px]"/>
          </div>
        </div>
        
        <h1 className="md:text-[32px] text-[8px] font-normal md:mt-8 mt-4">About | Terms of Use | Privacy | Contact | © 2025 Farmers Networks</h1>
      </div>
    </div>
  );
}

export default ContractMain;